import './JobReportGuestLandingPage.scss';

import { IonCol, IonContent, IonGrid, IonPage, IonRow } from '@ionic/react';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useGA4 } from '../../utils/ga4';
import stoneAgeLogo from '../../assets/images/logo-dm-stoneage.png';
import { GenericMessages } from '../../intl/GenericMessages';
import { useIntl } from 'react-intl';
import SupportCard from '../../components/experience-card/SupportCard';
import { contactLink } from '../../constants';
import { openBrowser } from '../../utils/browser';
import { GuestSignInCard } from './GuestSignInCard';
import { GenerateCodeCard } from './GenerateCodeCard';
import { ShareLinkExpiredCard } from './ShareLinkExpiredCard';
import { EmailAddressMissingCard } from './EmailAddressMissingCard';
import { CognitoUser } from '@aws-amplify/auth';
import { selectAuthUserInfo } from '../../redux/auth/selectors';
import { useSelector } from 'react-redux';
import { TosPageForGuest } from '../TosPageForGuest';

/**
 * Page that will display when a guest user clicks a share link in an email.
 *
 * Will stay in play until the user is signed in, either via security code, or
 * via username/password. Displays two cards, an action/error message card and
 * a StoneAge support card.  The two actions are: generate a security code, and
 * signing in by entering a security code.
 *
 * @constructor
 */
export const JobReportGuestLandingPage: React.FC = () => {
  document.title = 'Job Report Guest Landing Page'; // Purposely English only for Google Analytics
  useGA4();

  const { formatMessage } = useIntl();
  const history = useHistory();

  const [generateCodeClicked, setGenerateCodeClicked] = useState(false);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser>();
  const [loginSucceeded, setLoginSucceeded] = useState(false);

  const [expiresAtTimestamp, emailAddress] = useMemo(() => {
    const urlParams = new URLSearchParams(history.location.search);

    return [urlParams.get('data'), urlParams.get('sharedWith')];
  }, [history.location.search]);

  // URL includes expiration date so it can be checked before user gets to GenerateCodePage, hence "per URL"
  const [expiresAtPerUrl, isExpiredPerUrlDate] = useMemo(() => {
    let expiresAtPerUrl = 0;
    let isExpiredPerUrlDate = false;
    try {
      if (expiresAtTimestamp) {
        expiresAtPerUrl = Number(expiresAtTimestamp);
        isExpiredPerUrlDate = expiresAtPerUrl < Date.now();
      }
    } catch (err) {
      // Assume date is not expired. The real expiration check occurs later.
    }

    return [expiresAtPerUrl, isExpiredPerUrlDate];
  }, [expiresAtTimestamp]);

  const whichCard = useMemo(() => {
    if (isExpiredPerUrlDate) return 'share-link-expired';

    if (generateCodeClicked) return 'guest-sign-in';

    if (!emailAddress) return 'email-address-missing';

    return 'generate-code';
  }, [emailAddress, isExpiredPerUrlDate, generateCodeClicked]);

  const onCodeRequestSent = useCallback(async cognitoUser => {
    setCognitoUser(cognitoUser);
    setGenerateCodeClicked(true);
  }, []);

  const guestUser = useSelector(selectAuthUserInfo);

  const onLoginSuccess = useCallback(() => {
    setLoginSucceeded(true);
  }, [setLoginSucceeded]);

  if (loginSucceeded) {
    if (guestUser && !guestUser.tosAcceptedAt) {
      return <TosPageForGuest />;
    } else {
      const pathComponents = history.location.pathname.split('/');
      history.push(`/shared-job-reports/${pathComponents[2]}?sharedWith=${emailAddress}`);
    }
  }

  return (
    <IonPage>
      <IonContent className="security-code-page">
        <img className="landing-page-logo" src={stoneAgeLogo} alt={formatMessage(GenericMessages.stoneAgeLogo)} />
        <IonGrid className={'security-code-page-grid'}>
          <IonRow>
            <IonCol size="12" sizeMd="7">
              {whichCard === 'generate-code' && <GenerateCodeCard onCodeRequestSent={onCodeRequestSent} />}
              {whichCard === 'guest-sign-in' && cognitoUser && emailAddress && (
                <GuestSignInCard
                  cognitoUser={cognitoUser}
                  onLoginSuccess={onLoginSuccess}
                  emailAddress={emailAddress}
                />
              )}
              {whichCard === 'share-link-expired' && <ShareLinkExpiredCard expiresAt={expiresAtPerUrl} />}
              {(whichCard === 'email-address-missing' || !emailAddress) && <EmailAddressMissingCard />}
            </IonCol>
            <IonCol size="12" sizeMd="5">
              <SupportCard
                buttonText={formatMessage(contactLink.buttonText)}
                title={formatMessage(contactLink.title)}
                subtitle={formatMessage(contactLink.subtitle)}
                onClick={() => openBrowser(contactLink.actionUrl)}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};
